import { expandTokens } from "./normalizer";
import { stem } from "./ukStemmer";
import { CATS, NOUNS, VERBS, WEIGHTS, REALTY_HINTS, PROFS } from "./rules";

// Levenshtein
function levenshtein(a,b){
  if(a===b) return 0;
  const m=a.length, n=b.length;
  const dp=Array.from({length:m+1},()=>Array(n+1).fill(0));
  for(let i=0;i<=m;i++) dp[i][0]=i;
  for(let j=0;j<=n;j++) dp[0][j]=j;
  for(let i=1;i<=m;i++){
    for(let j=1;j<=n;j++){
      const cost = a[i-1]===b[j-1]?0:1;
      dp[i][j]=Math.min(dp[i-1][j]+1, dp[i][j-1]+1, dp[i-1][j-1]+cost);
    }
  }
  return dp[m][n];
}

function approxHit(token, roots, maxEdit=1){
  for (const r of roots){
    if (token.startsWith(r)) return {root:r, via:"prefix"};
    const dist = levenshtein(token, r);
    if (dist<=maxEdit) return {root:r, via:"fuzzy", dist};
  }
  return null;
}

export function classifyCategory(input){
  const tokens = expandTokens(input).map(t=>stem(t));
  if (tokens.length===0) return {category: null, score:0, reasons:[]};

  const score = {};
  const reasons = [];

  const hasAny = (rootsArr) => tokens.some(t => rootsArr.some(v => t.startsWith(v)));

  // intents
  const isRent     = hasAny(VERBS.RENT);
  const isBuySell  = hasAny(VERBS.BUY) || hasAny(VERBS.SELL);
  const isGive     = hasAny(VERBS.GIVE);
  const isSearch   = hasAny(VERBS.SEARCH);

  // career/services
  const isHireIntent  = hasAny(VERBS.HIRE);
  const isOfferIntent = hasAny(VERBS.OFFER);

  // 1) noun hits
  for (const [cat, roots] of Object.entries(NOUNS)){
    for (const t of tokens){
      const hit = approxHit(t, roots, 1);
      if (hit){
        score[cat] = (score[cat]||0) + WEIGHTS.nounHit;
        reasons.push(`noun:${t}→${cat} (${hit.via}${hit.dist!==undefined?` d=${hit.dist}`:""})`);
      }
    }
  }

  // 2) prof words → boost career & services
  for (const t of tokens){
    const h = approxHit(t, PROFS, 1);
    if (h){
      score[CATS.CAREER]   = (score[CATS.CAREER]||0)   + WEIGHTS.profHit;
      score[CATS.SERVICES] = (score[CATS.SERVICES]||0) + WEIGHTS.profHit;
      reasons.push(`prof:${t}→(Карʼєра & Послуги)`);
    }
  }

  // 3) buy/sell boost
  if (isBuySell){
    [
      CATS.REALTY, CATS.TRANSPORT, CATS.GADGETS, CATS.FASHION,
      CATS.HOME, CATS.GARDEN, CATS.PARTS, CATS.ACCESSORIES, CATS.FOOD
    ].forEach(cat=>{
      if (!cat) return;
      score[cat] = (score[cat]||0) + WEIGHTS.verbBuySell;
    });
    reasons.push("buy/sell boost");
  }

  // 4) rent boost
  if (isRent){
    score[CATS.RENT] = (score[CATS.RENT]||0) + WEIGHTS.verbRent*2;
    reasons.push("rent-verb boost");
  }

  // 5) giveaway hint
  if (isGive){
    [CATS.HOBBY, CATS.HOME, CATS.KIDS, CATS.PETS].forEach(cat=>{
      if (!cat) return;
      score[cat] = (score[cat]||0) + WEIGHTS.miscBoost;
    });
    reasons.push("giveaway boost");
  }

  // 6) realty hints
  if (REALTY_HINTS.some(h => typeof h==="string" ? tokens.includes(stem(h)) : h.test(input))){
    score[CATS.REALTY] = (score[CATS.REALTY]||0) + WEIGHTS.miscBoost;
    reasons.push("realty hints");
  }

  // 7) career vs services priority
  if (isHireIntent){
    score[CATS.CAREER] = (score[CATS.CAREER]||0) + WEIGHTS.hireBoost;
    reasons.push("hire intent → Карʼєра");
  }
  if (isOfferIntent){
    score[CATS.SERVICES] = (score[CATS.SERVICES]||0) + WEIGHTS.offerBoost;
    reasons.push("offer intent → Майстри та послуги");
  }

  // 8) select best
  let bestCat = null, bestScore = 0;
  for (const [cat,val] of Object.entries(score)){
    if (val>bestScore){ bestCat=cat; bestScore=val; }
  }

  // 9) tie: rent vs itemized
  if (isRent && bestCat && bestCat!==CATS.RENT){
    const strongItems = [CATS.REALTY, CATS.TRANSPORT, CATS.GADGETS, CATS.HOME, CATS.GARDEN];
    if (strongItems.includes(bestCat)) {
      bestCat = CATS.RENT;
      reasons.push("tie-breaker: rent verb → Оренда та прокат");
    }
  }

  // 10) tie: career vs services
  const hasProfWord = tokens.some(t => approxHit(t, PROFS, 1));
  if (hasProfWord){
    if (isHireIntent && bestCat !== CATS.CAREER){
      bestCat = CATS.CAREER;
      reasons.push("tie-breaker: проф + hire → Карʼєра");
    } else if (isOfferIntent && bestCat !== CATS.SERVICES){
      bestCat = CATS.SERVICES;
      reasons.push("tie-breaker: проф + offer → Майстри та послуги");
    }
  }

  // 11) tie: fashion vs accessories
  if (bestCat === CATS.FASHION && NOUNS[CATS.ACCESSORIES]){
    const accHit = tokens.some(t => approxHit(t, NOUNS[CATS.ACCESSORIES], 1));
    if (accHit){
      bestCat = CATS.ACCESSORIES;
      reasons.push("tie-breaker: аксесуари > стиль");
    }
  }

  // 12) tie: home vs garden
  if (bestCat === CATS.HOME && NOUNS[CATS.GARDEN]){
    const gardenHit = tokens.some(t => approxHit(t, NOUNS[CATS.GARDEN], 1));
    if (gardenHit){
      bestCat = CATS.GARDEN;
      reasons.push("tie-breaker: сад/подвір'я > дім");
    }
  }

  return { category: bestCat, score: bestScore, reasons };
}
